import { ExportService } from './../../services/export.service';
import { Component, Input, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SimulationTrackerItem } from '../../interfaces/simulation-tracker-item';
import { SimulationTrackerStatusMessage } from '../../enums/simulation-tracker-status';
import { SimulationTrackerDataService } from '../../services/simulation-tracker-data.service';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { Select, Store } from '@ngxs/store';
import { SimulationTrackerActions } from 'src/app/state/simulation-tracker/simulation-tracker.actions';
import { SimulationTrackerState } from 'src/app/state/simulation-tracker/simulation-tracker.state';
import { Router } from '@angular/router';
import { Table } from 'primeng/table';
import {
  FeatureFlagKey,
  FeaturesState,
  ParsedFeatureValue,
} from '../../../state/features/features.state';
import numWords from 'num-words';
import { UserState } from 'src/app/state/user/user.state';
import { DialogService } from 'primeng/dynamicdialog';
import { SimulationBulkExportComponent } from '../../components/simulation-bulk-export/simulation-bulk-export.component';

/**
 * Component representing a simulation tracker table.
 */
@Component({
  selector: 'app-simulation-tracker-table',
  templateUrl: './simulation-tracker-table.component.html',
  styleUrl: './simulation-tracker-table.component.scss',
})
export class SimulationTrackerTableComponent implements OnInit {
  /**
   * Creates an instance of SimulationTrackerTableComponent.
   * @param router The router service for navigation.
   */

  loggedInUserName = this.store.selectSnapshot(UserState.userProfile).name; // used to track the logged in user email


  constructor(
    private router: Router,
    private exportService: ExportService,
    private store: Store,
    private readonly confirmationService: ConfirmationService,
    private readonly simulationDataService: SimulationTrackerDataService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly messageService: MessageService,
    readonly dialogService: DialogService

  ) { }

  @Select(SimulationTrackerState.favoritesAvailable)
  favoritesAvailable$!: Observable<boolean>;
  @Select(SimulationTrackerState.favoritesLimit)
  favoritesLimit$!: Observable<number>;

  /**
   * Indicates if the component is in card mode.
   */
  @Input() cardMode: boolean = true;


  /**
   * Number of days the simulation data is retained.
   */
  @Input() simulationDaysRetained: number = 10;

  /**
   * Observable stream of simulation tracker items.
   */
  @Input() simulationItems$: Observable<SimulationTrackerItem[]> =
    new Observable<SimulationTrackerItem[]>();

  simulationItems: SimulationTrackerItem[] = []; // local copy for modification
  /**
   * Holds the status messages for the simulation tracker.
   * This property is of the type `SimulationTrackerStatusMessage`.
   */
  statusMessage: typeof SimulationTrackerStatusMessage =
    SimulationTrackerStatusMessage;

  /**
   * Flag to control the display of the dialog.
   */
  display: boolean = false;

  /**
   * Number of rows per page.
   */
  rows: number = 10;

  /**
   * Paginator dropdown options.
   */
  paginatorDropdownOptions = [5, 10, 20, 30, 50];

  /**
   * required this selectedSimulations to show number of selected simulations from checkboxes.
   */
  selectedSimulations: number = 0;

  /**
   * Indicates whether favorites are available for the simulation tracker table based on the favorites limit.
   * When set to `true`, the favorites functionality is enabled and accessible.
   */
  favoritesAvailable: boolean = true;
  favoritesLimit: number = 3;
  currentPage: number = 0;
  rowsPerPage: number = 10;



  /**
   * Initializes the component and sets up the paginated simulations observable.
   */
 ngOnInit(): void {

    this.simulationItems$.subscribe((newItems: SimulationTrackerItem[]) => {
      const previousSelections = new Map(
        this.simulationItems.map(item => [item.simulationId, item.selected])
      );

      this.simulationItems = newItems.map(item => ({
        ...item,
        selected: previousSelections.get(item.simulationId) ?? false
      }));

      this.updateAllSelectedState();

      this.changeDetectorRef.detectChanges();  // now tell Angular about the changes
    });

    const favLimit: ParsedFeatureValue | undefined = this.store.selectSnapshot(
      FeaturesState.selectFeatureNumberByKey(
        FeatureFlagKey.FavoriteSimulationLimit
      )
    );
    if (favLimit) {
      this.favoritesLimit = favLimit;
    }

    this.favoritesAvailable$.subscribe(
      (available) => (this.favoritesAvailable = available)
    );
    this.favoritesLimit$.subscribe((limit) => (this.favoritesLimit = limit));


  }

  ngOnDestroy(): void {
    this.messageService.clear();
  }


  /**
   * Displays the dialog.
   */
  showDialog() {
    this.display = true;
  }

  /**
   * Frozen columns in the table.
   */
  frozenColumns: number[] = [8];

  /**
   * Gets the theme for the given simulation tracker status.
   * @param status The simulation tracker status.
   * @returns The theme associated with the status.
   */
  getStatusTheme(status: SimulationTrackerStatusMessage): string {
    return SimulationTrackerDataService.GetStatusTheme(status);
  }

  /**
   * Gets the status as a string.
   * @param status The simulation tracker status.
   * @returns The status as a string.
   */
  getStatus(status: SimulationTrackerStatusMessage): string {
    return status;
  }

  /**
   * Gets the icon for the given simulation tracker status.
   * @param status The simulation tracker status.
   * @returns The icon associated with the status.
   */
  getStatusIcon(status: SimulationTrackerStatusMessage): string {
    return SimulationTrackerDataService.GetStatusIcon(status);
  }

  /**
   * Formats the expiration date.
   * @param expiration The expiration date.
   * @returns The formatted expiration date.
   */
  formatExpiration(expiration: Date | null | undefined): string {
    return SimulationTrackerDataService.FormatExpiration(expiration);
  }

  disableActionButton(simulationTrackerItem: SimulationTrackerItem): boolean {
    return !simulationTrackerItem.deleted;
  }

  /**
   * Exports the simulation data for the given simulation ID.
   * @param simulationId The unique identifier of the simulation to be exported.
   * @returns The simulation ID that was passed in.
   */
  exportSimulation(simulationId: string, simulationName?: string, scenario?: string) {
    this.exportService.exportSimulationResultFiles(simulationId, simulationName, scenario);
  }

  /**
   * Marks the given simulation tracker as a favorite.
   * @param simulationTracker The simulation tracker to be marked as favorite.
   * @returns The simulation tracker that was marked as favorite.
   */
  favorite(simulationTracker: SimulationTrackerItem) {
    this.store
      .dispatch(
        new SimulationTrackerActions.FavoriteSimulationTracker(
          simulationTracker.simulationTrackerId
        )
      )
      .pipe(
        finalize(() =>
          this.store.dispatch(
            new SimulationTrackerActions.GetFavoritedSimulationTrackers()
          )
        )
      )
      .subscribe()
  }



  removeFavorite(simulationTracker: SimulationTrackerItem) {
    this.store
      .dispatch(
        new SimulationTrackerActions.RemoveFavoritedSimulationTracker(
          simulationTracker.simulationTrackerId
        )
      )
      .pipe(
        finalize(() =>
          this.store.dispatch(
            new SimulationTrackerActions.GetFavoritedSimulationTrackers()
          )
        )
      )
      .subscribe()
  }

  /**
   * Menu items for the tracker.
   */
  trackerMenuItems: MenuItem[] = [];

  /**
  * Menu items for the Bulk Action tracker.
  */
  bulkActionTrackerMenu: MenuItem[] = [];

  cannotFavoriteItem(simulationTracker: SimulationTrackerItem): boolean {
   const isOwner = this.loggedInUserName === simulationTracker.userName;

  const isNotCompleted =
    simulationTracker.statusMessage !== SimulationTrackerStatusMessage.Completed 

  return (
    !isOwner ||
    simulationTracker.favorited ||
    !this.favoritesAvailable ||
    isNotCompleted
  );
  }
  /**
   * Sets the tracker menu items based on the given simulation tracker.
   * @param simulationTracker The simulation tracker to set the menu for.
   */
  setTrackerMenu(simulationTracker: SimulationTrackerItem): void {
    this.trackerMenuItems = [
      {
        label: 'Favorite',
        icon: 'symbol-star',
        disabled: this.cannotFavoriteItem(simulationTracker),
        tooltipOptions: {
          tooltipLabel: `${this.favoritesLimit} Favorites Maximum`,
        },
        command: () => this.favorite(simulationTracker),
      },
      {
        label: 'Remove Favorite',
        icon: 'symbol-star outlined',
        disabled: !simulationTracker.favorited,
        command: () => this.removeFavorite(simulationTracker),
      },
      {
        label: 'View',
        icon: 'symbol-remove-red-eye',
        disabled:
          simulationTracker.statusMessage !==
          SimulationTrackerStatusMessage.Completed,
        command: () => {
          this.navigateToResult(simulationTracker);
        },
      },
      {
        label: 'Delete',
        disabled: this.cannotDeleteItem(simulationTracker),
        icon: 'symbol-delete',
        command: () => this.deleteTracker(simulationTracker),
      },
    ];
  }
  cannotDeleteItem(simulationTracker: SimulationTrackerItem): boolean {
    return (this.loggedInUserName != simulationTracker.userName);
  }
  deleteTracker(simulationTracker: SimulationTrackerItem) {
    this.confirmationService.confirm({
      header: 'Confirmation',
      message: `Are you sure you want to delete the "${simulationTracker.studyAreaName}" simulation?`,
      accept: () => {
        this.store.dispatch(
          new SimulationTrackerActions.DeleteMultipleSimulationTracker(
            [simulationTracker.simulationId]
          )
        ).subscribe(() => {
          // ✅ Remove from local list
          this.simulationItems = this.simulationItems.filter(
            item => item.simulationId !== simulationTracker.simulationId
          );

          // ✅ Reset table pagination and filters
          this.filteredSimulations = [];
          this.table.reset();
          this.updateAllSelectedState();
          this.store.dispatch(new SimulationTrackerActions.GetSimulationTrackers());
        });
      },
      reject: () => { },
      acceptIcon: '.',
      rejectIcon: '.',
      rejectButtonStyleClass: 'p-button-text',
      acceptButtonStyleClass: 'p-button-text basin-ml-5 basin-color-danger',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
    });
  }

  /**
   * navigate to the result of the navigation tracker
   * @param simulationTracker tracker to navigate to
   */
  navigateToResult(simulationTracker: SimulationTrackerItem) {
    this.router.navigate(
      this.simulationDataService.getRouteForSimulationTrackerViewing(
        simulationTracker
      )
    );
  }

  @ViewChild(Table) private table!: Table;

  searchStringChange(searchString: string): void {
    if (!this.table) return;
    this.table.filterGlobal(searchString, 'contains');
  }

  tagList = [
    { name: 'Favorite' },
    { name: 'Scenario' },
  ];

  tagFilterValues: string[] = [];

  onTagFilterChange(): void {
    if (!this.table) return;

    if (this.tagFilterValues.length === 0) {
      this.table.filterGlobal('', 'contains');
      return;
    }

    const combinedTags: string = this.tagFilterValues.join(' ');
    this.table.filterGlobal(combinedTags, 'contains');
  }

  dfpAccessList = [
    { name: 'Yes' },
  ];

  dfpAccessFilterValues: any[] = [];

  onDFPAccessFilterChange(): void {
    if (!this.table) return;

    if (this.dfpAccessFilterValues.length === 0) {
      this.table.filterGlobal('', 'contains');
      return;
    }

    const combinedTags: string = this.dfpAccessFilterValues.join(' ');
    this.table.filterGlobal(combinedTags, 'contains');
  }

  convertNumberToWords(number: number): string {
    if (number < 0) {
      return 'Negative ' + numWords(Math.abs(number));
    }
    return numWords(number);
  }


  filteredSimulations: SimulationTrackerItem[] = [];
  allSelected = false;

  onTableFilter(event: any) {
    this.filteredSimulations = event.filteredValue ?? this.simulationItems;
  }

  onPageChange(event: any) {
    this.currentPage = event.page;
    this.rowsPerPage = event.rows;
    this.allSelected = false;
    // Optionally reset global "selected" flags if you don't want cross-page memory
    // this.simulationItems.forEach(row => row.selected = false);

    this.updateAllSelectedState();
  }

  onRowCheckboxChange() {
    this.updateAllSelectedState();
  }

  onFilterChange(event: any) {
    // Update currentPageRows based on filtered value if needed 
    this.updateAllSelectedState();
  }

  updateAllSelectedState() {
    const visibleRows = this.currentPageRows;
    this.allSelected = visibleRows.length > 0 && visibleRows.every(row => row.selected);
  }

  toggleAllSelections() {
    for (const row of this.currentPageRows) {
      if (row.statusMessage == SimulationTrackerStatusMessage.Completed) {
        row.selected = this.allSelected;

      }
    }
  }

  async downloadSelectedSimulations() {
    const selectedIds = this.simulationItems
      .filter(row => row.selected && row.statusMessage == SimulationTrackerStatusMessage.Completed)
      .map(row => row.simulationId);

    if (selectedIds.length === 0) {
      alert("No simulations selected on this page.");
    }
    else {
      this.exportService.exportMultipleSimulationsZip(selectedIds);
    }
  }

  get anyRowSelected(): boolean {
    this.selectedSimulations = this.simulationItems.filter(row => row.selected).length;
    return this.simulationItems?.some(row => row.selected);
  }

  get currentPageRows(): SimulationTrackerItem[] {
    if (!this.table) return [];
    const start = this.table.first ?? 0;
    const rows = this.table.rows ?? this.rows;

    const source = this.filteredSimulations.length > 0
      ? this.filteredSimulations
      : this.simulationItems;

    return source.slice(start, start + rows);
  }

  /**
   * Sets the tracker menu items based on the given simulation bulk action tracker.
   * @param bulkActionTrackerMenu The simulation tracker to set the menu for.
   */
  setBulkActionTrackerMenu(): void {
    this.bulkActionTrackerMenu = [
      {
        label: 'Export',
        icon: 'symbol-cloud-download',
        command: () => this.downloadSelectedSimulations(),
      },
      {
        label: 'Delete',
        icon: 'symbol-delete',
        command: () => this.handleDeleteCommand(),
      },
    ];
  }

  handleDeleteCommand(): void {
    const selectedSimulations = this.getselectedSimulations();

    // Filter simulations not owned by the current user
    const invalidSimulations = selectedSimulations.filter(sim => sim.userName !== this.loggedInUserName);

    const loggedUserSimulations = selectedSimulations.filter(sim => sim.userName === this.loggedInUserName);

    const message = this.getDeleteConfirmationMessage(loggedUserSimulations, invalidSimulations);

    if (loggedUserSimulations.length === 0) {
      this.confirmationService.confirm({
        header: 'Confirmation',
        message: message,
        accept: () => {
          this.resetFiltersAndPagination();
          this.store.dispatch(new SimulationTrackerActions.GetSimulationTrackers());
        },
        reject: () => { },
        acceptIcon: '.',
        rejectIcon: '.',
        rejectButtonStyleClass: 'p-button-text',
        acceptButtonStyleClass: 'p-button-text basin-ml-5 basin-color-danger',
        rejectVisible: false,
        acceptLabel: 'Ok',
      });
    }
    else {
      this.confirmationService.confirm({
        header: 'Confirmation',
        message: message,
        accept: () => this.onDeleteAccept(loggedUserSimulations),
        reject: () => { },
        acceptIcon: '.',
        rejectIcon: '.',
        rejectButtonStyleClass: 'p-button-text',
        acceptButtonStyleClass: 'p-button-text basin-ml-5 basin-color-danger',
        acceptLabel: 'Delete',
        rejectLabel: 'Cancel',
      });
    }
  }

  private getDeleteConfirmationMessage(selectedSimulations: SimulationTrackerItem[], invalidSimulations: SimulationTrackerItem[]): string {
    const count = selectedSimulations.length;

    // Extract simulation names and join them into a comma-separated string
    const invalidNames = invalidSimulations.map(sim => sim.simulationName).join(', ');
    const simulationLabel = invalidSimulations.length === 1 ? 'simulation' : 'simulations';

    if (count === 0 && invalidSimulations.length > 0) {
      return `You do not have permission to delete the ${simulationLabel}: ${invalidNames}. Please select simulations you own.`;
    }
    if (count === 1) {
      if (selectedSimulations[0].simulationName != null) {
        return `You do not have permission to delete the ${simulationLabel}: ${invalidNames}. The remaining ${selectedSimulations[0].simulationName} simulation will be deleted. Are you sure you want to proceed?`;
      }
      else {
        return `You do not have permission to delete the ${simulationLabel}: ${invalidNames}. The remaining simulation will be deleted. Are you sure you want to proceed?`;
      }
    }
    else {
      if (count > 1) {
        if (invalidSimulations.length > 0) {
          return `You do not have permission to delete the ${simulationLabel}: ${invalidNames}. The remaining ${count} simulations will be deleted. Are you sure you want to proceed?`;
        }
        else {
          return `Are you sure you want to delete ${count} simulations?`;
        }
      }
    }
    return ""; // Default case (should not reach here)
  }


  private onDeleteAccept(selectedSimulations: SimulationTrackerItem[]): void {
    const simulationsToDelete = selectedSimulations.map(sim => sim.simulationId);

    this.store.dispatch(
      new SimulationTrackerActions.DeleteMultipleSimulationTracker(simulationsToDelete)
    ).subscribe(() => {
      this.removeDeletedSimulations(simulationsToDelete);
      this.resetFiltersAndPagination();
      this.store.dispatch(new SimulationTrackerActions.GetSimulationTrackers());
    });
  }

  private removeDeletedSimulations(simulationsToDelete: string[]): void {
    this.simulationItems = this.simulationItems.filter(
      item => !simulationsToDelete.includes(item.simulationId)
    );
  }

  private resetFiltersAndPagination(): void {
    this.filteredSimulations = [];
    this.table.reset();
    this.updateAllSelectedState();
  }


  getselectedSimulations() {
    return this.simulationItems
      .filter(row => row.selected && row.statusMessage == SimulationTrackerStatusMessage.Completed);
  }

  handleKeyDown(event: KeyboardEvent) {
    // Your logic here
    console.log('Key down event:', event.key);
  }

  openExportByTypeModal(): void {
    const selectedIds = this.simulationItems
      .filter(row => row.selected && row.statusMessage == SimulationTrackerStatusMessage.Completed)
      .map(row => row.simulationId);

    this.dialogService.open(
      SimulationBulkExportComponent,
      {
        width: '620px',
        header: `Export`,
        dismissableMask: true,
        styleClass: 'bottom-rounded',
        style: {
          position: 'absolute',
        },
        data: {
          data: selectedIds
        }
      }
    );
  }

  onFilterKeyDown(event: KeyboardEvent) {
    console.log('Key down:', event.key);
  }

  onFilterKeyUp(event: KeyboardEvent) {
    console.log('Key up:', event.key);
  }

  onFilterKeyPress(event: KeyboardEvent) {
    console.log('Key press:', event.key);
  }

  refreshGrid(): void {
     this.store.dispatch(new SimulationTrackerActions.GetSimulationTrackers());
 
}
}



